/**
 * `ParsedCommand` → `RuntimeAction` narrowing (architecture.md §7.2 step 1).
 *
 * @remarks
 * Structure only — narrowing decides WHAT the player is attempting, never
 * whether the world permits it (visibility, locks, and conditions are steps
 * 2–3, in `executeTurn`). Everything here is a pure function of the proposal
 * itself; unknown intents become typed rejections, never throws.
 *
 * The load-bearing asymmetry (R2 handoff, "open vs use"): `use X on Y`
 * carries its INSTRUMENT as the direct object, while `open Y with X` carries
 * its TARGET as the direct object — both narrow onto the one `use` action
 * (instrument X, target Y), which is how "unlock/open are aliases of use"
 * (design doc §1) is honored one layer below vocabulary. A target reached
 * through EITHER the `on` binding (indirect object) or the `with`/`using`
 * bindings (tool) is accepted, so "use the keycard with the reader" means
 * what the player meant.
 *
 * `drop` is in the base vocabulary but v0 has no put-it-back-down world
 * model (`WorldState` records removals, not re-placements) — it narrows to a
 * typed `unsupported_intent` rejection with an honest line (ruled with the
 * R5 plan sign-off, 2026-08-13).
 *
 * @packageDocumentation
 */

import type { DoorRefusal, ItemDefinition, ItemId, RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { ActionRejectionReason, CommandNarrowingOutcome, RuntimeAction } from './runtime-action.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { speakSlot } from '../defaults/default-responses.js';
import { itemIdForEntityRef, roomIdForEntityRef } from '../entities/entity-ref.js';
import { type SpeakableLine, splitSpokenLines } from '../execution/action-handler-result.js';
import { isItemUnreachable, unreachableRefusalParts } from '../execution/reachability.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { narrowActorAimedCommand } from './narrow-actor-aimed.js';
import { narrowAskCommand } from './narrow-ask-command.js';
import { narrowGiveCommand } from './narrow-give-command.js';
import { narrowGoCommand } from './narrow-go-command.js';
import { answersUsedAlone, impliedInstrumentOf, narrowInstrumentOnTarget } from './narrow-instrument.js';
import {
	narrowBodyAimedCommand,
	narrowRoomAimedCommand,
	narrowSetCommand,
	narrowSwitchCommand,
	refuseRoomAimedVerb,
} from './narrow-intercepts.js';
import { narrowOrderCommand } from './narrow-order.js';
import { narrowRemoveCommand } from './narrow-remove-command.js';
import { narrowSearchCommand, narrowSingleObjectCommand } from './narrow-single-object.js';
import { narrowAuthoredVerb, refuseUnsupportedIntent, THING_REFUSED_OUTRIGHT_INTENT_TOKENS } from './narrow-verb-answers.js';

/** The directives that take no object and narrow to a constant action. */
const OBJECT_FREE_ACTIONS: Readonly<Record<string, RuntimeAction>> = {
	help: { type: 'help' },
	inventory: { type: 'inventory' },
	look: { type: 'look' },
	map: { type: 'map' },
	wait: { type: 'wait' },
};

/**
 * The directives that carry exactly one direct object and narrow to the same
 * shape — a table for the same reason as {@link OBJECT_FREE_ACTIONS}: when R25
 * gave `drop` mechanics, a fourth identical switch arm was the complexity
 * cap's way of asking for this.
 */
const SINGLE_OBJECT_ACTION_TYPES: Readonly<Record<string, 'drop' | 'equip' | 'inspect' | 'take' | 'unequip'>> = {
	// R25: setting a thing down — the take shape, pointed the other way.
	drop: 'drop',
	// D143: wearing and taking off — the carry family's third pair.
	equip: 'equip',
	inspect: 'inspect',
	take: 'take',
	unequip: 'unequip',
};

/**
 * Narrow one parsed proposal to a runtime action, or refuse it with a typed
 * rejection.
 *
 * @param command - The parser's structured proposal
 * @returns The narrowed action, or the rejection
 */
export const narrowParsedCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state?: RuntimeState,
): CommandNarrowingOutcome => {
	// The brand is parser-internal; narrowing switches on the canonical text.
	const intentText: string = command.intent;

	/*
	 * The INTERCEPTORS, in claiming order (see narrow-intercepts.ts): a SET
	 * (D217 — it reaches narrowing only when the pack answers it in one line;
	 * the walker has already split it for `each`), the room
	 * (D41 — carrying a room is not a thing anyone can do), the hands (D63 —
	 * the engine's own "with what?" must accept the answer a person gives), and
	 * the per-item switch (D56 — checked before the unsupported refusal below,
	 * because that refusal is exactly what a declared `switchable` overrides).
	 */
	// D232: a command that names an ACTOR anywhere is the actor intercept's
	// first — bearing or no bearing, because no arm below can hold a body:
	// looking narrows, speech falls through to `ask`, everything else refuses
	// in the actor's words (`narrow-actor-aimed.ts`).
	// D231: a clause with an explicit BEARING names its thing as a destination
	// (`enter the door`, `go up the road`), never as a verb's object — the
	// parser keeps the thing now, where D227 set it aside — so the room and
	// the hands do not claim it; the `go` arm decides what the thing means.
	// D252: a clause said TO a body is that body's order before it is anything else (`narrow-order.ts`).
	for (const intercept of [
		narrowOrderCommand,
		narrowActorAimedCommand,
		...(command.direction === undefined ? [narrowSetCommand, narrowRoomAimedCommand, narrowBodyAimedCommand, narrowSwitchCommand] : []),
	]) {
		const interceptedOutcome = intercept(command, bundle, intentText, state);

		if (interceptedOutcome !== undefined) {
			return interceptedOutcome;
		}
	}

	// D35: the VOCABULARY declares which verbs it recognizes without mechanics
	// ("kick", "break", "drop" in v0). They parse fully — object and all — and
	// are refused by name here, so a player who typed a genre-standard
	// directive is answered rather than accused of gibberish.
	// D234: a verb mechanized on a BODY and refused on a THING (`attack` — the
	// actor intercept above has claimed every command naming a body) earns
	// the same refusal here, one rung, so the two cannot drift.
	if (bundle.compiledLexicon.intentsByToken.get(intentText)?.isUnsupported === true || THING_REFUSED_OUTRIGHT_INTENT_TOKENS.includes(intentText)) {
		return refuseUnsupportedIntent(command, bundle, intentText, state);
	}

	// The object-free directives carry no structure to narrow, so a table says
	// so more plainly than four identical switch arms — and keeps the switch
	// below under the complexity cap as the verb roster grows (D50).
	const objectFreeAction = OBJECT_FREE_ACTIONS[intentText];

	if (objectFreeAction !== undefined) {
		return narrowed(objectFreeAction);
	}

	const singleObjectActionType = SINGLE_OBJECT_ACTION_TYPES[intentText];

	if (singleObjectActionType !== undefined) {
		return narrowSingleObjectCommand(command, bundle, state, singleObjectActionType);
	}

	switch (intentText) {
		case 'ask': {
			return narrowAskCommand(command);
		}
		// D50: shutting a door. Only a thing that HAS one can be shut, so the
		// refusal names the reason rather than pretending the verb is unknown.
		case 'close': {
			return narrowDoorCommand(command, bundle, state, 'close_container');
		}
		// D235: a thing handed or shown to nobody, or to a thing — the body case
		// never reaches here (the actor intercept has claimed it).
		case 'give':
		case 'show': {
			return narrowGiveCommand(command, bundle, state, intentText);
		}
		case 'go': {
			return narrowGoCommand(command, bundle, state);
		}
		case 'open': {
			return narrowOpenCommand(command, bundle, state);
		}
		case 'remove': {
			return narrowRemoveCommand(command, bundle, state, narrowSingleObjectCommand);
		}
		// D50: searching is its own act — a THING is gone through, and (D62) a
		// bare `search` cases the room, because the room is what you are
		// standing in when you do not say otherwise.
		case 'search': {
			return narrowSearchCommand(command, bundle, state);
		}
		case 'use': {
			return narrowUseCommand(command, bundle, state);
		}
		default: {
			return narrowAuthoredVerb(command, bundle, state, intentText);
		}
	}
};

/** `open Y with X` — direct object is the TARGET, tool (or indirect object) is the instrument. */
const narrowOpenCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome => {
	if (command.directObjectRef === undefined) {
		return rejected(bundle, state, { detail: 'open arrived without a direct object', type: 'malformed_command' }, [
			speakSlot(bundle, state, 'malformedCommand'),
		]);
	}

	const targetItemId = itemIdForEntityRef(command.directObjectRef);

	// R29/D106 (Calder's §4.1): the NEAREST barrier speaks first — an
	// unreachable target must refuse at reach before any of the arms below
	// answer a question about instruments or doors, because "with what?"
	// through glass invites a tool hunt the glass makes pointless (and a
	// question no answer can satisfy is D39's own trap).
	if (state !== undefined && isItemUnreachable(bundle, state, targetItemId)) {
		const refusalParts = unreachableRefusalParts(bundle, state, targetItemId);

		return rejected(bundle, state, refusalParts.reason, refusalParts.speakable);
	}

	const instrumentRef = command.toolRef ?? command.indirectObjectRef;

	// D225 (E1): the room named as the INSTRUMENT — `open the door with the
	// ruts`. The intercept read the direct object and saw the door, so the
	// room reaches this path, and it is not an item id: `narrowInstrumentOnTarget`
	// would hand it to `requireItem` and the turn would throw instead of
	// answering. It answers in the room's own sentence.
	if (instrumentRef !== undefined && roomIdForEntityRef(instrumentRef) !== undefined) {
		return refuseRoomAimedVerb(command, bundle, 'open', state);
	}

	if (instrumentRef === undefined) {
		// D135: the thing's own word on `open` comes first — the stair door
		// with its handle on the far side is a door in the fiction and no lid
		// in the engine, and only the pack knows which sentence is true of it.
		const openRefusal = applicableDoorRefusal(requireItem(bundle, targetItemId).openRefusal, bundle, state);

		if (openRefusal !== undefined) {
			// D269: a lock that refuses the hand opens to the one carried thing
			// that opens it — `open the guard` with Ryland's slate in hand is `use
			// the slate on the guard`, said so, exactly as the named act would go.
			const impliedInstrumentItemId = impliedInstrumentOf(bundle, state, targetItemId);

			return impliedInstrumentItemId === undefined
				? rejected(bundle, state, { itemId: targetItemId, type: 'open_refused' }, openRefusal.narratives)
				: narrowed({ instrumentItemId: impliedInstrumentItemId, isInstrumentImplied: true, targetItemId, type: 'use' });
		}

		// D50: a thing with its OWN door opens by hand — the audit's §1 closed
		// here. D53 adds the qualifier that made it safe: a door may declare
		// WHEN hands are enough, and a door that refuses them falls through to
		// the instrument path below, exactly as it behaved before doors existed.
		// Without that, giving the wall locker a door handed the player a way
		// around the pry bar.
		if (canOpenByHand(requireItem(bundle, targetItemId), bundle, state)) {
			return narrowed({ itemId: targetItemId, type: 'open_container' });
		}

		// D39: a question no answer can satisfy is a trap. If the pack authored
		// NO interaction against this thing, nothing the player could name would
		// ever work — so refuse honestly instead of inviting an answer.
		if (!isInteractionTarget(bundle, targetItemId)) {
			const target = requireItem(bundle, targetItemId);

			// D135 (Calder's locker-bank §4): a thing with NO LID is a fact about
			// the thing, not about the player's inventory. "Nothing here would open
			// the benches" was the missing-instrument register speaking about a
			// bench, and it sent players hunting a key that does not exist; the
			// `close` side already had the true sentence. A thing WITH a lid that
			// nothing opens keeps D39's honest refusal — that one really is about
			// what would open it.
			if (target.openable === undefined) {
				return rejected(bundle, state, { itemId: targetItemId, type: 'not_openable' }, [
					speakSlot(bundle, state, 'notOpenable', { itemName: target.name }),
				]);
			}

			return rejected(bundle, state, { targetItemId, type: 'no_interaction_possible' }, [
				speakSlot(bundle, state, 'nothingOpensThat', {
					itemName: target.name,
					verb: command.intentSurface.toLowerCase(),
				}),
			]);
		}

		return rejected(bundle, state, { targetItemId, type: 'missing_instrument' }, [
			speakSlot(bundle, state, 'missingInstrument', verbInterpolationsOf(command)),
		]);
	}

	return narrowInstrumentOnTarget(itemIdForEntityRef(instrumentRef), targetItemId, bundle, state);
};

/**
 * D53: are bare hands enough for this door, right now?
 *
 * @remarks
 * A thing with no door cannot be hand-opened at all. A door with no
 * `handOpenCondition` always yields — most doors are just doors. A door that
 * declares one yields only while it holds, and otherwise `open` falls through
 * to the instrument path, which is how a tool puzzle survives its target
 * gaining a lid.
 *
 * State is optional because narrowing is called from tooling and tests without
 * it; absent, a conditional door is treated as NOT hand-openable. That is the
 * safe direction — a caller with no world to consult must not be handed a
 * bypass.
 *
 * @param item - The thing being opened
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @returns Whether bare hands open it now
 */
export const canOpenByHand = (item: ItemDefinition, bundle: EngineGameBundle, state: RuntimeState | undefined): boolean => {
	if (item.openable === undefined) {
		return false;
	}

	if (item.openable.handOpenCondition === undefined) {
		return true;
	}

	return state !== undefined && evaluateCondition(item.openable.handOpenCondition, bundle, state);
};

/**
 * D50: `close X` — and, by symmetry, any door verb that needs a door.
 *
 * @remarks
 * `close` was a declared-unsupported verb until containment gave it something
 * to mean. It is now real, but only for things that HAVE a door: closing a
 * collapsed shelf is not a refusal about the verb, it is a fact about the
 * shelf, and `not_openable` says which.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param type - The door action to build
 * @returns The narrowed action, or the typed refusal
 */
const narrowDoorCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	// CLOSE only, and deliberately narrow. `open X` reaches `open_container`
	// through the use/open path above, which has a whole ladder of its own
	// (an instrument, a lock, a container), so this is the shutting half and
	// nothing else. Typed as the union it used to be, every `type === 'close_container'`
	// question below had an arm nothing could enter — a generality with no
	// second case is not a generality, it is dead weight (§2.1). If `open` is
	// ever routed here, the parameter widens again on purpose.
	type: 'close_container',
): CommandNarrowingOutcome => {
	if (command.directObjectRef === undefined) {
		return rejected(bundle, state, { detail: `${type} arrived without a direct object`, type: 'malformed_command' }, [
			speakSlot(bundle, state, 'malformedCommand'),
		]);
	}

	const itemId = itemIdForEntityRef(command.directObjectRef);
	const item = requireItem(bundle, itemId);

	// R29/D106: reach before the door — you cannot shut what you cannot touch.
	if (state !== undefined && isItemUnreachable(bundle, state, itemId)) {
		const refusalParts = unreachableRefusalParts(bundle, state, itemId);

		return rejected(bundle, state, refusalParts.reason, refusalParts.speakable);
	}

	// D135: `close X` on a thing that does not close — the grille once pried,
	// the security door once carded, locker eleven once forced — refuses in
	// the item's own words. On the ITEM rather than inside `openable`, because
	// two of the POC's three one-way things carry their open state in a flag
	// and have no door to hang it on (Calder's item-seven doc §1.1). The
	// `when` gate is what lets the grille say "already shut" before it is
	// pried and "shut is not a shape this one has any more" after.
	const closeRefusal = applicableDoorRefusal(item.closeRefusal, bundle, state);

	if (closeRefusal !== undefined) {
		return rejected(bundle, state, { itemId, type: 'close_refused' }, closeRefusal.narratives);
	}

	if (item.openable === undefined) {
		return rejected(bundle, state, { itemId, type: 'not_openable' }, [
			speakSlot(bundle, state, 'notOpenable', { itemName: item.name }),
		]);
	}

	return narrowed({ itemId, type });
};

/**
 * D135: an item's `openRefusal` / `closeRefusal`, when it applies right now.
 *
 * @remarks
 * Absent a `when`, always (a bench that will never close; a door whose
 * handle is on the other side). Present, only while it holds — and, as with
 * `canOpenByHand`, a caller with no state to consult is answered in the safe
 * direction: a conditioned refusal is treated as NOT applying, so tooling and
 * tests without a world never see a refusal the world did not earn.
 *
 * @param refusal - The item's authored refusal for this verb, if any
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @returns The refusal to speak, or `undefined` when the verb may proceed
 */
export const applicableDoorRefusal = (
	refusal: DoorRefusal | undefined,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
): DoorRefusal | undefined => {
	if (refusal === undefined) {
		return undefined;
	}

	if (refusal.when === undefined) {
		return refusal;
	}

	return state !== undefined && evaluateCondition(refusal.when, bundle, state) ? refusal : undefined;
};

/**
 * The player's own verb, offered to a question that parks (D230, Calder's
 * Salt Debt §3).
 *
 * @remarks
 * "Use it on what?" answered `leave coin`, and a pack whose `use` is mostly
 * leaving had to claim the slot verb-free ("Where, or on what?") because
 * `{verb}` was printed raw there — the two parked questions were the only
 * refusals built on a parsed command that offered it nothing. They offer the
 * three casings every other refusal does: `{verb}` inside prose, `{intent}`
 * shouted, and `{Verb}` opening the sentence ("Leave it where?").
 *
 * @param command - The parsed command the question is about
 * @returns The verb as typed, in the interpolator's two source casings
 */
const verbInterpolationsOf = (command: ParsedCommand): { readonly intent: string; readonly verb: string } => ({
	intent: command.intentSurface.toUpperCase(),
	verb: command.intentSurface.toLowerCase(),
});

/** `use X on Y` — direct object is the INSTRUMENT; `on`/`with`/`using` reach the target. */
const narrowUseCommand = (command: ParsedCommand, bundle: EngineGameBundle, state: RuntimeState | undefined): CommandNarrowingOutcome => {
	if (command.directObjectRef === undefined) {
		return rejected(bundle, state, { detail: 'use arrived without a direct object', type: 'malformed_command' }, [
			speakSlot(bundle, state, 'malformedCommand'),
		]);
	}

	const instrumentItemId = itemIdForEntityRef(command.directObjectRef);
	const targetRef = command.indirectObjectRef ?? command.toolRef;

	// D245: the thing used on its own answers in its own words when one of its
	// target-free arms holds — `use the autodoc` — and otherwise asks, as it always has.
	if (targetRef === undefined) {
		return answersUsedAlone(bundle, state, instrumentItemId)
			? narrowed({ instrumentItemId, type: 'use' })
			: rejected(bundle, state, { instrumentItemId, type: 'missing_target' }, [
				speakSlot(bundle, state, 'missingTarget', verbInterpolationsOf(command)),
			]);
	}

	// D225 (E1): the room named as the TARGET — Drumline's `use the chain on
	// the ruts`, the ruts being the track's own noun. Same reason as the open
	// path above: the instrument hid the room from the intercept, and a room
	// is nothing a tool meets.
	if (roomIdForEntityRef(targetRef) !== undefined) {
		return refuseRoomAimedVerb(command, bundle, 'use', state);
	}

	return narrowInstrumentOnTarget(instrumentItemId, itemIdForEntityRef(targetRef), bundle, state);
};

/**
 * Does the pack author ANY interaction against this item (D39)? Bundle data
 * only — no state — so the answer is the same wherever the player stands: an
 * item nothing acts upon can never be opened, whatever they name.
 *
 * @param bundle - The loaded bundle
 * @param targetItemId - The thing the player aimed at
 * @returns Whether some authored interaction targets it
 */
const isInteractionTarget = (bundle: EngineGameBundle, targetItemId: ItemId): boolean => {
	for (const item of bundle.indexes.itemsById.values()) {
		if (item.useInteractions.some((interaction) => interaction.targetItemId === targetItemId)) {
			return true;
		}
	}

	return false;
};

/** Wrap an action as the successful narrowing outcome. */
export const narrowed = (action: Extract<CommandNarrowingOutcome, { type: 'narrowed' }>['action']): CommandNarrowingOutcome => ({
	action,
	type: 'narrowed',
});

/**
 * Build the rejected narrowing outcome (v0: rejections never consume a turn).
 *
 * @remarks
 * D58: accepts authored lines and slot responses mixed, and records which
 * slots spoke so the seam that commits state can advance their pools — the
 * same split the handler-side `rejected` performs. R15: the narratives
 * RESOLVE here, at creation — a rejection changes no state, so the world now
 * is the world at stamping time, and the seam hands out what the transcript
 * will say (Calder's §5.2). Absent a state (tooling, tests), fallbacks speak.
 *
 * @param bundle - The loaded bundle (conditional readings resolve against it)
 * @param state - The live runtime state, when the caller has one
 * @param reason - The typed reason
 * @param speakable - Authored lines and/or slot responses, in speaking order
 * @returns The rejected outcome
 */
export const rejected = (
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	reason: ActionRejectionReason,
	speakable: readonly SpeakableLine[],
): CommandNarrowingOutcome => {
	const { lines, spokenSlots } = splitSpokenLines(speakable);

	return {
		rejection: {
			consumesTurn: false,
			narratives: resolveNarrativeLines(lines, bundle, state),
			reason,
			...(spokenSlots.length === 0 ? {} : { spokenSlots }),
		},
		type: 'rejected',
	};
};
